import { useEffect, useState } from 'react';
import { balanceService, type PeriodoBalance } from '../services/balanceService';
import { movimientoFondoCampanaService } from '../services/movimientoFondoCampanaService';
import type { OwnerFondoCampana, SaldoRestringidoCampana } from '../types/fondosCampana';
import type { Movimiento } from '../lib/supabase';
import { formatCurrency, formatDate } from '../utils/formatters';
import { composicionDelFondo, etiquetaMovimientoFondo } from '../utils/fondosCampanaPresentacion';
import { ComprobantesDeMovimiento } from './comprobantes/ComprobantesDeMovimiento';
import { MovimientoCampanaModal } from './MovimientoCampanaModal';
import { FormErrorAlert } from './ui/FormErrorAlert';
import { EnviosCampanaPanel } from './EnviosCampanaPanel';

interface Props {
  readonly ownerContext: OwnerFondoCampana;
  readonly puedeOperar: boolean;
  readonly periodo?: PeriodoBalance;
  readonly mostrarEnvios?: boolean;
}
interface Datos {
  readonly fondos: SaldoRestringidoCampana[];
  readonly destinos: ReadonlySet<string>;
  readonly movimientos: Movimiento[];
  /** Instante de la lectura: de él depende si un comprobante pendiente figura vencido. */
  readonly leidoEn: Date;
}
function Desglose({ fondo }: { readonly fondo: SaldoRestringidoCampana }) {
  return <details className="mt-2 text-xs font-normal text-slate-600"><summary className="cursor-pointer">Ver composición</summary>
    <dl className="mt-2 space-y-1">{composicionDelFondo(fondo).map(([label, valor]) => <div key={label} className="flex justify-between gap-4"><dt>{label}</dt><dd>{formatCurrency(valor)}</dd></div>)}</dl>
  </details>;
}
export function FondosCampanaTabla(props: Props) {
  return <Tabla key={`${props.ownerContext.tipo}:${props.ownerContext.id}:${props.periodo?.tipo}:${props.periodo?.año}:${props.periodo?.mes}`} {...props} />;
}
function Tabla({ ownerContext: owner, puedeOperar, periodo, mostrarEnvios = true }: Props) {
  const [datos, setDatos] = useState<Datos | null>(null);
  const [error, setError] = useState('');
  const [revision, setRevision] = useState(0);
  const [gasto, setGasto] = useState<string | null>(null);
  useEffect(() => {
    let vigente = true;
    Promise.all([balanceService.getSaldosRestringidosCampana(owner), movimientoFondoCampanaService.getCampanasDestinoFinal(owner),
      periodo ? balanceService.getMovimientosFondosCampana(owner, periodo) : Promise.resolve([])])
      .then(([fondos, destinos, movimientos]) => { if (vigente) { setDatos({ fondos, destinos: new Set(destinos.map(c => c.id)), movimientos, leidoEn: new Date() }); setError(''); } })
      .catch((error_: unknown) => { if (vigente) setError(error_ instanceof Error ? error_.message : 'No pudimos cargar los fondos de campañas.'); });
    return () => { vigente = false; };
  }, [owner, periodo, revision]);
  const recargar = () => { setRevision(r => r + 1); };
  return <section className="space-y-5 rounded-xl border border-slate-200 bg-white p-5" aria-label="Fondos de campañas">
    <div><h3 className="text-xl font-semibold text-slate-800">Fondos de campañas</h3>
      <p className="mt-1 text-sm text-slate-600">Saldos actuales de {owner.nombre}. Son fondos separados de la caja libre y del mes seleccionado. Las entregas en especie no constituyen efectivo.</p></div>
    <FormErrorAlert error={error} />
    {error && <button type="button" onClick={recargar} className="rounded-lg border px-3 py-2">Reintentar</button>}
    {!datos && !error && <output className="block">Cargando saldos actuales…</output>}
    {datos?.fondos.length === 0 && <p>No hay fondos registrados para esta entidad.</p>}
    {datos && datos.fondos.length > 0 && <div className="overflow-x-auto"><table className="w-full text-sm">
      <thead><tr className="border-b text-left text-slate-600">{['Campaña', 'Saldo registrado', 'Enviado por confirmar', 'Enviado observado', 'Disponible', ''].map(t => <th key={t || 'acciones'} className="p-3">{t}</th>)}</tr></thead>
      <tbody>{datos.fondos.map(f => <tr key={f.campanaId} className="border-b align-top">
        <td className="p-3 font-medium">{f.nombre}<Desglose fondo={f} /></td>
        {[f.saldoRegistrado, f.enviadoPendiente, f.enviadoObservado, f.disponible].map((n, i) => <td key={['saldo', 'pendiente', 'observado', 'disponible'][i]} className="p-3 tabular-nums">{formatCurrency(n)}</td>)}
        <td className="p-3">{puedeOperar && datos.destinos.has(f.campanaId) && f.disponible > 0 && <button type="button" onClick={() => setGasto(f.campanaId)} className="rounded-lg border px-3 py-2">Registrar gasto</button>}</td>
      </tr>)}</tbody>
    </table></div>}
    {periodo && datos && <details><summary className="cursor-pointer font-medium">Movimientos del período seleccionado</summary>
      <p className="my-2 text-sm text-slate-600">La apertura se identifica aparte; no es recaudación nueva.</p>
      {datos.movimientos.length === 0 && <p>No hubo movimientos del fondo en este período.</p>}
      <ul className="divide-y">{datos.movimientos.map(m => <li key={m.id} className="py-2 text-sm">
        {formatDate(m.fecha)} · {etiquetaMovimientoFondo(m)} · {formatCurrency(m.monto)} · {m.descripcion}
        {!m.anulado && <div className="mt-1"><ComprobantesDeMovimiento movimiento={m} ahora={datos.leidoEn} /></div>}
      </li>)}</ul>
    </details>}
    {mostrarEnvios && <EnviosCampanaPanel owner={owner} readOnly={!puedeOperar} onRefresh={recargar} />}
    <MovimientoCampanaModal isOpen={gasto !== null} onClose={() => setGasto(null)} onSuccess={recargar} ownerContext={owner} campanaId={gasto ?? undefined} />
  </section>;
}
